import {
    MutableRefObject,
    RefObject,
    memo,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useWindowSize } from 'react-use';
import cn from 'classnames';
import {
    ICON_MATCH_EVENT,
    IEvent,
    IEventType,
    MATCH_EVENT,
} from '@zmedia/bm-soccer-ui/interface';
import { TIMER_GET_LIVE, URL_SOCCER_PAGE } from '@zmedia/bm-soccer-ui/constants';
import { ArrayUtils, Logger, ObjectUtils } from '@zmedia/bm-soccer-ui/utils';
import MatchService from '@zmedia/bm-soccer-ui/service/MatchService';
import { LiveService } from '@zmedia/bm-soccer-ui/service/LiveService';
import {
    Columns,
    Element,
    Icon,
    Level,
    List,
    RouterLink,
    Scrollbars,
    Section,
    Title,
} from '@zmedia/bm-soccer-ui/components';
import { Avatar } from '@zmedia/bm-soccer-ui/modules/shared/components/avatar';
import PenShootout from './PenShootout';
import styles from './style.module.scss';

export function EventSection(props: any) {
    const { id, hasPenShootout, isLive, eventsMatch = {} } = props;
    const [data, setData] = useState<any>(eventsMatch);
    const timeout = useRef<any>(0);

    const sectionRef: MutableRefObject<any> = useRef<RefObject<any>>(null);
    const eventSectionRef: MutableRefObject<any> = useRef<RefObject<any>>(null);
    const typesRef: MutableRefObject<any> = useRef<RefObject<any>>(null);

    const [height, setHeight] = useState<string>('calc(100% - 22rem)');
    const [showScroll, setShowScroll] = useState<boolean>(false);

    const size = useWindowSize();

    const fetchData = (id: number) => {
        const fetch = isLive ? LiveService.getEvents : MatchService.getEvents;
        fetch(id)
            .then((resp) => {
                if (resp && resp.data) {
                    const { items = [] } = resp.data;
                    if (!ArrayUtils.isEmpty(items)) {
                        const data = resp.data || {};
                        setData(data);
                    }
                    if (isLive) {
                        timeout.current = setTimeout(() => {
                            fetchData(id);
                        }, TIMER_GET_LIVE);
                    }
                }
            })
            .catch((err) => {
                if (err) {
                    Logger.error(err.err, 'getEventsInMatch fail|', err);
                }
            });
    };

    useEffect(() => {
        if (ObjectUtils.isEmpty(eventsMatch) && id) {
            fetchData(id);
        }
        return () => {
            clearTimeout(timeout.current);
        };
    }, [id, isLive]);

    const { items = [], types = [] } = data;

    const renderEvent = (event: IEvent) => {
        if (event.type.id === MATCH_EVENT.SUBSTITUTION) {
            return (
                <>
                    <RouterLink
                        className={cn(
                            styles['event-player'],
                            styles['is-main']
                        )}
                        to={`${URL_SOCCER_PAGE.SUBPATH}${event.player?.path}`}
                        title={event?.player?.name}
                    >
                        <Avatar
                            avatar={event?.player?.avatar}
                            name={event?.player?.name}
                        />
                        <Element className={styles['text']}>
                            {event?.player?.shortName}
                        </Element>
                        <Icon icon="sub-in"></Icon>
                    </RouterLink>

                    {event.playerExtra && (
                        <RouterLink
                            className={styles['event-player']}
                            to={`${URL_SOCCER_PAGE.SUBPATH}${event.playerExtra?.path}`}
                            title={event?.playerExtra?.name}
                        >
                            <Avatar
                                avatar={event?.playerExtra?.avatar}
                                name={event?.playerExtra?.name}
                            />
                            <Element className={styles['text']}>
                                {event?.playerExtra?.shortName}
                            </Element>
                            <Icon icon="sub-out"></Icon>
                        </RouterLink>
                    )}
                </>
            );
        }

        if (event.type.id === MATCH_EVENT.GOAL) {
            return (
                <>
                    <RouterLink
                        className={cn(
                            styles['event-player'],
                            styles['is-main']
                        )}
                        to={`${URL_SOCCER_PAGE.SUBPATH}${event.player?.path}`}
                        title={event?.player?.name}
                    >
                        <Avatar
                            avatar={event?.player?.avatar}
                            name={event?.player?.name}
                        />
                        <Element className={styles['text']}>
                            {event?.player?.shortName}
                        </Element>
                        <Icon icon="goal"></Icon>
                    </RouterLink>

                    {event.playerExtra && (
                        <RouterLink
                            className={styles['event-player']}
                            to={`${URL_SOCCER_PAGE.SUBPATH}${event.playerExtra?.path}`}
                            title={event?.playerExtra?.name}
                        >
                            <Avatar
                                avatar={event?.playerExtra?.avatar}
                                name={event?.playerExtra?.name}
                            />
                            <Element className={styles['text']}>
                                {event?.playerExtra?.shortName}
                            </Element>
                            <Icon icon="assist"></Icon>
                        </RouterLink>
                    )}
                </>
            );
        }

        return (
            <RouterLink
                className={styles['event-player']}
                to={`${URL_SOCCER_PAGE.SUBPATH}${event.player?.path}`}
                title={event?.player?.name}
            >
                <Avatar
                    avatar={event?.player?.avatar}
                    name={event?.player?.name}
                />
                <Element className={styles['text']}>
                    {event?.player?.shortName}
                </Element>
                <Icon
                    icon={ICON_MATCH_EVENT[MATCH_EVENT[event.type.id]]}
                ></Icon>
            </RouterLink>
        );
    };
    const renderEvents = () => {
        return items.map((item: IEvent, index: number) => {
            if (!item.player) {
                return (
                    <List.Item className={styles['event-text']} key={index}>
                        <Element className={styles['text-wrapper']}>
                            <Icon icon="whistle"></Icon>
                            <Element>{item.type.name}</Element>
                        </Element>
                        <Element>{item.note}</Element>
                    </List.Item>
                );
            }
            return (
                <List.Item key={index}>
                    <Level>
                        <Level.Side align="left">
                            {item.fromTeam === 'home' ? (
                                <>{renderEvent(item)}</>
                            ) : (
                                <>&nbsp;</>
                            )}
                        </Level.Side>
                        <Level.Side align="center">
                            <Element className={styles['time']}>
                                {item.minuteLabel}
                                {item.aScore && item.hScore && (
                                    <Element className={styles['scores']}>
                                        <Element
                                            className={cn({
                                                [styles['is-goal']]:
                                                    item.fromTeam === 'home',
                                            })}
                                        >
                                            {item.hScore}
                                        </Element>
                                        -
                                        <Element
                                            className={cn({
                                                [styles['is-goal']]:
                                                    item.fromTeam === 'away',
                                            })}
                                        >
                                            {item.aScore}
                                        </Element>
                                    </Element>
                                )}
                            </Element>
                        </Level.Side>
                        <Level.Side align="right">
                            {item.fromTeam === 'away' ? (
                                <>{renderEvent(item)}</>
                            ) : (
                                <>&nbsp;</>
                            )}
                        </Level.Side>
                    </Level>
                </List.Item>
            );
        });
    };

    const renderTypes = useCallback(() => {
        return types.map((type: IEventType) => {
            return (
                <Element className={styles['type-item']} key={type.id}>
                    <Icon icon={ICON_MATCH_EVENT[MATCH_EVENT[type.id]]}></Icon>
                    <Element>{type.name}</Element>
                </Element>
            );
        });
    }, [types]);

    useEffect(() => {
        if (sectionRef.current && eventSectionRef.current && typesRef.current) {
            const parentHeight = eventSectionRef.current?.offsetHeight;
            const childHeight = sectionRef.current?.offsetHeight;
            const typesHeight = typesRef.current?.offsetHeight;

            const heightScroll = parentHeight - typesHeight - 48;

            setHeight(heightScroll + 'px');

            if (childHeight > heightScroll) {
                setShowScroll(true);
            }
        }
    }, [items]);

    if (ArrayUtils.isEmpty(items)) {
        return null;
    }

    return (
        <Columns.Column>
            <Section
                className={styles['event-section']}
                domRef={eventSectionRef}
                name="match-event"
            >
                <Title renderAs="h2">Diễn biến</Title>
                <Element
                    className={styles['event-container']}
                    style={{
                        borderBottom: showScroll ? '1px solid #f5f5f5' : '',
                        height: size.width > 768 ? height : 'auto',
                    }}
                >
                    <Scrollbars
                        autoHide
                        autoHideTimeout={1000}
                        autoHideDuration={200}
                        disableScrollParent={true}
                        showScroll={showScroll}
                        id={'event-scroll'}
                    >
                        <Element domRef={sectionRef}>
                            {hasPenShootout && (
                                <PenShootout id={id} isLive={isLive} />
                            )}
                            <List>{renderEvents()}</List>
                        </Element>
                    </Scrollbars>
                </Element>
                <Element className={styles['event-footer']} domRef={typesRef}>
                    <List className={styles['type-items']}>
                        {renderTypes()}
                    </List>
                </Element>
            </Section>
        </Columns.Column>
    );
}

export default memo(EventSection);
